/**
 * Application entry point wired with TanStack Router so that all front-end routes share a single HTML shell.
 *
 * The Bun server serves `index.html` for every non-API path and this router selects the correct React tree.
 */

import "./index.css";

import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  Outlet,
  RouterProvider,
  createRoute,
  createRootRoute,
  createRouter,
  useParams,
} from "@tanstack/react-router";

import App from "./App";
import { ConfigureScreenApp } from "./pages/configure/configure-screen-app";
import { DebugScreenApp } from "./pages/debug/debug-screen-app";
import { FaqPage } from "./pages/faq/faq-page";

type ReactRoot = ReturnType<typeof createRoot>;

const STANDALONE_CONFIG_DEVICE_ID_STORAGE_KEY = "tilee-standalone-config-device-id";

const isStandaloneDisplayMode = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
};

const RootRouteComponent = () => <Outlet />;

const NotFoundComponent = () => (
  <main data-app-shell="tilee" className="min-h-screen grid place-items-center bg-background text-foreground p-6">
    <section className="text-center space-y-2">
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="text-muted-foreground">The page you were looking for does not exist.</p>
    </section>
  </main>
);

const HomeRouteComponent = () => {
  useEffect(() => {
    if (!isStandaloneDisplayMode()) {
      return;
    }

    try {
      const savedPublicDeviceId = window.localStorage.getItem(STANDALONE_CONFIG_DEVICE_ID_STORAGE_KEY);

      if (!savedPublicDeviceId) {
        return;
      }

      const nextPathname = `/configure/screen/${savedPublicDeviceId}`;

      if (window.location.pathname === nextPathname) {
        return;
      }

      window.location.replace(nextPathname);
    } catch (error) {
      console.error("Failed to restore standalone configure screen", {
        code: "STANDALONE_CONFIG_RESTORE_FAILED",
        error,
      });
    }
  }, []);

  return <App />;
};

const FaqsRouteComponent = () => <FaqPage />;

const DebugScreenRouteComponent = () => {
  const { publicDeviceId } = useParams({ from: "/debug/screen/$publicDeviceId" });
  return <DebugScreenApp publicDeviceId={publicDeviceId} />;
};

const ConfigureScreenRouteComponent = () => {
  const { publicDeviceId } = useParams({ from: "/configure/screen/$publicDeviceId" });
  return <ConfigureScreenApp publicDeviceId={publicDeviceId} />;
};

const rootRoute = createRootRoute({
  component: RootRouteComponent,
  notFoundComponent: NotFoundComponent,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRouteComponent,
});

const faqsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/faqs",
  component: FaqsRouteComponent,
});

const debugScreenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/debug/screen/$publicDeviceId",
  component: DebugScreenRouteComponent,
});

const configureScreenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/configure/screen/$publicDeviceId",
  component: ConfigureScreenRouteComponent,
});

const routeTree = rootRoute.addChildren([indexRoute, faqsRoute, debugScreenRoute, configureScreenRoute]);

/*
 * Builds a TanStack Router instance bound to the statically defined route tree.
 * Inputs: none.
 * Outputs: A configured router ready for use by the RouterProvider.
 */
const createAppRouter = () => createRouter({ routeTree });

type AppRouterInstance = ReturnType<typeof createAppRouter>;

/*
 * Renders the router provider tree within React.StrictMode.
 * Inputs: routerInstance - the router that should service the current render.
 * Outputs: JSX element tree for ReactDOM to mount.
 */
const renderApp = (routerInstance: AppRouterInstance) => (
  <StrictMode>
    <RouterProvider router={routerInstance} />
  </StrictMode>
);

type HotStore = {
  root?: ReactRoot;
  router?: AppRouterInstance;
};

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found in document");
}

if (import.meta.hot) {
  const hotStore = (import.meta.hot.data ??= {}) as HotStore;

  const router = hotStore.router ?? createAppRouter();
  hotStore.router = router;

  const root = hotStore.root ?? createRoot(rootElement);
  hotStore.root = root;
  root.render(renderApp(router));

  import.meta.hot.dispose((data) => {
    const store = (data as HotStore | undefined) ?? {};
    store.root = root;
    store.router = router;
  });
} else {
  const router = createAppRouter();
  createRoot(rootElement).render(renderApp(router));
}
